import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  boardEndsAt,
  ENGULF_DURATION_MS,
  ENGULF_TAPS,
  ENGULF_WINDOW_MS,
  screeningChannel,
  type BoardScreening,
  type HypeMember,
} from '@movienight/shared';
import { getBoard, toggleHype } from '../lib/api';
import { useSocket } from '../lib/SocketProvider';

/**
 * The Now Showing board's data, and everything live about it.
 *
 * ## What makes it refetch
 *
 *  1. **`screening_announced`** over the socket. The event carries an id and
 *     nothing else — the socket says *something changed*, REST says *what*
 *     (DESIGN.md §14).
 *  2. **Reconnect**, through the provider's shared `onResync`. Registering here
 *     rather than opening a second socket is why `SocketProvider` fans out.
 *  3. **A screening ending.** The tick below drives the countdown line and
 *     doubles as the drop-off check: when a card's night is over the board is
 *     refetched rather than filtered locally, so what is on screen is always
 *     something the server actually sent.
 *  4. **`hype_changed`**, debounced. The event's `count` is authoritative and
 *     lands immediately; the *faces* are not in the payload, so the list is
 *     reconciled by a refetch a moment later. Debounced because a group
 *     arguing about a film produces a burst of these, and forty people each
 *     triggering a refetch per tap is the one way this gets expensive.
 *  5. **Seats**, on the same debounce. `seat_booked` and `seat_freed` name a
 *     seat, not a count, so "3 od 7 slobodno" is refetched rather than
 *     guessed at — and `room_map_changed` is what takes REZERVIŠI from
 *     disabled to live the moment a room is finally mapped.
 *
 * ## Tapping a flame
 *
 * The flame flips **locally and instantly**, and the write is what waits. A
 * burst of taps therefore costs at most one request — usually none, since an
 * even number of taps lands back where it started — and the eight-tap easter
 * egg does not fire eight round trips on its way to setting the card alight.
 * `serverHype` is what the write is compared against: local state is what you
 * see, that ref is what the server last told us, and a request only happens
 * when they disagree after the tapping stops.
 */

export type BoardState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; screenings: BoardScreening[] };

const TICK_MS = 30_000;
/** Quiet period after the last tap before the toggle is actually written. */
const TAP_SETTLE_MS = 320;
/**
 * The wildcard reveal (DESIGN.md §12): the card turns away, the film lands,
 * the card turns back. Two halves rather than one keyframe, because the swap
 * has to happen at the *edge* — a single animation would show the new poster
 * arriving through the face of the old card.
 */
const FLIP_AWAY_MS = 320;
const FLIP_BACK_MS = 360;
/**
 * Trailing window for the things the socket reports but does not describe:
 * the avatar list after someone else's tap, and the free-seat count after
 * somebody books. Both are bursty — a group arguing about a film, or a room
 * filling up in the two minutes after an announcement — and one refetch per
 * event is the single way this screen gets expensive.
 */
const REFETCH_MS = 900;

/** Which half of the flip a card is in. See `reveal` below. */
export type FlipPhase = 'away' | 'back';

export interface BoardApi {
  board: BoardState;
  now: Date;
  refresh: () => void;
  /** Screenings currently on fire. Cosmetic, and never persisted anywhere. */
  engulfed: ReadonlySet<string>;
  /** Wildcards mid-reveal. Empty almost always; two seconds a month. */
  revealing: ReadonlyMap<string, FlipPhase>;
  /** One tap on your own flame. Toggles locally; the network catches up. */
  tapHype: (screeningId: string) => void;
}

export function useBoard(me: HypeMember): BoardApi {
  const { socket, onResync } = useSocket();
  const [board, setBoard] = useState<BoardState>({ status: 'loading' });
  const [now, setNow] = useState(() => new Date());
  const [engulfed, setEngulfed] = useState<ReadonlySet<string>>(() => new Set());
  const [revealing, setRevealing] = useState<ReadonlyMap<string, FlipPhase>>(() => new Map());
  const alive = useRef(true);

  // Read by the tick and by the event handlers without becoming dependencies,
  // which would restart the interval and resubscribe on every fetch.
  const current = useRef<BoardScreening[]>([]);
  const serverHype = useRef(new Map<string, { mine: boolean; count: number }>());
  const taps = useRef(new Map<string, number[]>());
  const settleTimers = useRef(new Map<string, number>());
  const burnTimers = useRef(new Map<string, number>());
  const refetchTimer = useRef<number | undefined>(undefined);
  /** The last fetch failed. Read by the tick, which is the only thing that retries. */
  const failed = useRef(false);

  const apply = useCallback((screenings: BoardScreening[]) => {
    current.current = screenings;
    setBoard({ status: 'ready', screenings });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await getBoard();
      for (const s of data.screenings) {
        serverHype.current.set(s.id, { mine: s.hype.mine, count: s.hype.count });
      }
      apply(data.screenings);
      failed.current = false;
      // The server's clock, not the phone's. Someone whose device is twenty
      // minutes fast should not see "u toku" on a film that has not started.
      setNow(new Date(data.now));
    } catch (err) {
      // Recorded in a ref as well as in the state, because the tick below reads
      // it and must not become a dependency of this callback.
      failed.current = true;
      setBoard({
        status: 'error',
        message: err instanceof Error ? err.message : 'Ne mogu da učitam raspored.',
      });
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => onResync(() => void refresh()), [onResync, refresh]);

  /** Rewrite one screening's hype in place, leaving every other card alone. */
  const patchHype = useCallback(
    (id: string, fn: (hype: BoardScreening['hype']) => BoardScreening['hype']) => {
      const next = current.current.map((s) => (s.id === id ? { ...s, hype: fn(s.hype) } : s));
      apply(next);
    },
    [apply],
  );

  const scheduleRefetch = useCallback(() => {
    window.clearTimeout(refetchTimer.current);
    refetchTimer.current = window.setTimeout(() => void refresh(), REFETCH_MS);
  }, [refresh]);

  const burn = useCallback((id: string) => {
    setEngulfed((prev) => new Set(prev).add(id));
    window.clearTimeout(burnTimers.current.get(id));
    burnTimers.current.set(
      id,
      window.setTimeout(() => {
        setEngulfed((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        burnTimers.current.delete(id);
      }, ENGULF_DURATION_MS),
    );
  }, []);

  /**
   * The wildcard reveal (DESIGN.md §12): "the card flips itself over on the
   * board to reveal the winner. Everyone with the page open watches it happen
   * live over the socket. This is one of the best moments on the site — build
   * it properly."
   *
   * Properly, here, means the swap is driven by the data landing rather than by
   * a timer that hopes it has. The card turns away and *holds* there —
   * `animation-fill-mode: forwards` — until both the refetch has come back and
   * the turn has actually had its 320ms. Only then does it turn back, with the
   * new film on it. A single keyframe with a refetch fired alongside is the
   * version that shows the old card on a slow night and the new poster arriving
   * edge-on through the face of it.
   *
   * The event is the *only* trigger. Nothing here compares `pitchClosesAt` to
   * the clock: the server decides when a vote is counted, and a client that
   * guessed would flip a card the server had not resolved yet.
   */
  const reveal = useCallback(
    async (id: string) => {
      setRevealing((prev) => new Map(prev).set(id, 'away'));
      await Promise.all([refresh(), delay(FLIP_AWAY_MS)]);
      if (!alive.current) return;
      setRevealing((prev) => new Map(prev).set(id, 'back'));
      await delay(FLIP_BACK_MS);
      if (!alive.current) return;
      setRevealing((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [refresh],
  );

  // --- socket events --------------------------------------------------------

  useEffect(
    () =>
      socket.on((event) => {
        if (event.name === 'screening_announced') {
          void refresh();
          return;
        }
        if (event.name === 'hype_changed') {
          const id = event.channel.slice('screening:'.length);
          const { userId, hyped, count } = event.payload;
          // The count is authoritative even for our own echo: two people
          // tapping at once means our optimistic +1 was one short.
          patchHype(id, (h) => ({ ...h, count, mine: userId === me.id ? hyped : h.mine }));
          if (userId !== me.id) {
            serverHype.current.set(id, {
              mine: serverHype.current.get(id)?.mine ?? false,
              count,
            });
            scheduleRefetch();
          }
          return;
        }
        /**
         * A seat was taken or given back. The card shows "3 od 7 slobodno",
         * and the event says which seat rather than what the count now is —
         * so the honest answer is to ask again, debounced.
         */
        if (event.name === 'seat_booked' || event.name === 'seat_freed') {
          scheduleRefetch();
          return;
        }
        /**
         * A room got its map. REZERVIŠI is disabled while a room has no
         * bookable seats, so this is the moment the button goes live on every
         * card in that room — without this, nobody finds out until they
         * reload.
         */
        if (event.name === 'room_map_changed') {
          scheduleRefetch();
          return;
        }
        if (event.name === 'card_engulfed') {
          burn(event.channel.slice('screening:'.length));
          return;
        }
        /**
         * A wildcard's vote was counted. The payload names the winner, and the
         * card still asks for the board — the socket says *something changed*,
         * REST says *what*. What the payload is for is the flip: it is the one
         * thing that has to start before the data arrives.
         */
        if (event.name === 'wildcard_resolved') {
          void reveal(event.payload.screeningId);
        }
      }),
    [socket, refresh, patchHype, scheduleRefetch, burn, reveal, me.id],
  );

  /**
   * Subscribe to exactly the screenings on the board, and to no others.
   *
   * Keyed on the joined id list rather than the array, so a refetch that
   * returns the same screenings does not tear every subscription down and put
   * it straight back up — which would drop any frame that arrived in between.
   */
  const ids = board.status === 'ready' ? board.screenings.map((s) => s.id) : [];
  const idKey = ids.join(',');
  useEffect(() => {
    if (idKey === '') return;
    const unsubscribes = idKey.split(',').map((id) => socket.subscribe(screeningChannel(id)));
    return () => {
      for (const off of unsubscribes) off();
    };
  }, [socket, idKey]);

  // --- tapping --------------------------------------------------------------

  const tapHype = useCallback(
    (id: string) => {
      const entry = current.current.find((s) => s.id === id);
      if (!entry) return;

      const mine = !entry.hype.mine;
      patchHype(id, (h) => ({
        count: Math.max(0, h.count + (mine ? 1 : -1)),
        mine,
        // Removing yourself is exact; adding yourself back is left to the
        // refetch, which is the only thing that knows the real order of faces.
        who: mine ? [...h.who, me] : h.who.filter((w) => w.id !== me.id),
      }));

      const at = Date.now();
      const recent = [...(taps.current.get(id) ?? []), at].filter(
        (t) => at - t < ENGULF_WINDOW_MS,
      );
      taps.current.set(id, recent);

      if (recent.length >= ENGULF_TAPS) {
        taps.current.set(id, []);
        // Locally first: the sender's own card should not wait for a round trip
        // to show what its own detector just fired. The hub excludes us.
        burn(id);
        socket.engulf(id);
      }

      window.clearTimeout(settleTimers.current.get(id));
      settleTimers.current.set(
        id,
        window.setTimeout(() => {
          settleTimers.current.delete(id);
          const known = serverHype.current.get(id);
          const local = current.current.find((s) => s.id === id)?.hype.mine ?? false;
          // Nothing to say: an even number of taps ends where it started.
          if (known && known.mine === local) return;
          void toggleHype(id)
            .then((res) => {
              serverHype.current.set(id, { mine: res.hyped, count: res.count });
              patchHype(id, (h) => ({ ...h, mine: res.hyped, count: res.count }));
            })
            .catch(() => void refresh());
        }, TAP_SETTLE_MS),
      );
    },
    [burn, me, patchHype, refresh, socket],
  );

  // --- the minute hand ------------------------------------------------------

  useEffect(() => {
    const id = window.setInterval(() => {
      const at = new Date();
      setNow(at);
      /**
       * Retry a board that failed.
       *
       * Without this the error state was a dead end: `current.current` is empty
       * after a failure, so the drop-off check below never fired, no socket
       * event was being waited on, and "Raspored se ne učitava" stayed on the
       * screen until somebody reloaded the page. One blip, one broken page.
       */
      if (failed.current) {
        void refresh();
        return;
      }
      if (current.current.some((s) => boardEndsAt(s).getTime() <= at.getTime())) {
        void refresh();
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const settle = settleTimers.current;
    const burns = burnTimers.current;
    alive.current = true;
    return () => {
      // The reveal is a pair of awaited delays rather than a stored timer, so
      // this flag is what stops it setting state on the way out.
      alive.current = false;
      for (const t of settle.values()) window.clearTimeout(t);
      for (const t of burns.values()) window.clearTimeout(t);
      window.clearTimeout(refetchTimer.current);
    };
  }, []);

  return useMemo(
    () => ({ board, now, refresh: () => void refresh(), engulfed, revealing, tapHype }),
    [board, now, refresh, engulfed, revealing, tapHype],
  );
}

const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));
